import React from 'react';
import { motion } from 'framer-motion';
import { ProductLine } from '../../data/productsLoader';
import { ProductCard } from './ProductCard';
import { Zap, Shield, Wind } from 'lucide-react';

interface RangeSectionProps {
  productLine: ProductLine;
  isAlternate?: boolean;
}

// 根据产品系列 ID 生成标签
function getProductTags(lineId: string): string[] {
  const tagMap: Record<string, string[]> = {
    'zwolf': ['ALL-TERRAIN', 'PREMIUM'],
    'zcougar': ['LUXURY', 'LONG-RANGE'],
    'zrino': ['PERFORMANCE', 'RUGGED'],
    'zlion': ['URBAN', 'BALANCED'],
    'zfox': ['CITY', 'LIGHT'],
    'ztoy': ['KIDS', 'SAFE']
  };
  return tagMap[lineId] || [];
}

export function RangeSection({ productLine, isAlternate = false }: RangeSectionProps) {
  const tags = getProductTags(productLine.id);
  return (
    <section id={productLine.id} className="scroll-mt-24 border-b border-gray-100 py-24 last:border-0">
      <div className="container mx-auto px-4">
        {/* Series Hero Section */}
        <div className={`flex flex-col lg:flex-row gap-12 items-center mb-16 ${isAlternate ? 'lg:flex-row-reverse' : ''}`}>

          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
                <span className="px-3 py-1 bg-black text-white text-xs font-bold rounded-full uppercase tracking-wider">
                  Serie {productLine.name}
                </span>
                {tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>

              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                {productLine.name}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                {productLine.description}
              </p>

              {/* Keywords */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6">
                {productLine.features.slice(0, 3).map((feature, idx) => (
                  <div key={idx} className="flex items-center text-sm font-medium text-gray-700 bg-gray-50 px-4 py-2 rounded-lg border border-gray-100">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 text-primary">
                      {idx === 0 ? <Zap size={14} /> : idx === 1 ? <Wind size={14} /> : <Shield size={14} />}
                    </div>
                    {feature}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Hero Image */}
          <div className="flex-1 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative aspect-square bg-gray-100 rounded-3xl overflow-hidden max-w-md mx-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-gray-200/50 to-transparent mix-blend-multiply"></div>
              <img
                src={productLine.coverImage || `/products/${productLine.id}.jpg`}
                alt={productLine.name}
                className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-700"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-scooter.png';
                }}
              />
            </motion.div>
          </div>
        </div>

        {/* Models Grid */}
        <div className={productLine.products.length === 1 ? 'max-w-md mx-auto' : ''}>
          <div className={productLine.products.length === 1 ? 'grid grid-cols-1 gap-8' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'}>
            {productLine.products.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
